import React, { useEffect, useState, useMemo } from 'react';
import { 
  Button, Box, Container, Paper, Typography, Chip, CircularProgress, Backdrop,
  TextField, MenuItem, Grid, InputAdornment, IconButton, Tooltip 
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import * as invoiceService from '../services/invoiceService';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { toast } from 'react-toastify';
import { calculateInvoiceTotals, formatDate as formatDateUtil } from '../utils/invoiceCalculations';
import { generateAndPreviewPDF, formatDate } from '../services/invoicePdfService';
import { useTranslation } from "react-i18next";
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';

const ListInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Filter states
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Get payment methods from Redux
  const { paymentMethods } = useSelector((state) => state.paymentMethod);
  
  // Get available quarters based on current month
  const getAvailableQuarters = () => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const quarters = [];
    
    if (currentMonth >= 3) quarters.push({ value: 'q1', label: 'Q1 (Jan-Mar)' });
    if (currentMonth >= 6) quarters.push({ value: 'q2', label: 'Q2 (Apr-Jun)' });
    if (currentMonth >= 9) quarters.push({ value: 'q3', label: 'Q3 (Jul-Sep)' });
    if (currentMonth >= 12) quarters.push({ value: 'q4', label: 'Q4 (Oct-Dec)' });
    
    return quarters;
  };
  
  const availableQuarters = getAvailableQuarters();
  
  // ✅ Get store info from Redux store (auto-fetches if needed)
  const { storeInfo, loading: storeLoading } = useStoreInfo();
  
  // Helper function to aggregate payment methods
  const getAggregatedPayments = React.useCallback((payments) => {
    if (!payments || payments.length === 0) return [];
    
    const paymentMap = {};
    payments.forEach((payment) => {
      const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method';
      const amount = Number(payment.Amount || 0);
      
      if (paymentMap[methodName]) {
        paymentMap[methodName] += amount;
      } else {
        paymentMap[methodName] = amount;
      }
    });
    
    return Object.entries(paymentMap).map(([methodName, totalAmount]) => ({
      methodName,
      totalAmount
    }));
  }, []);
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const navigate = useNavigate();

  // Fetch payment methods on mount
  useEffect(() => {
    if (paymentMethods.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, paymentMethods.length]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await invoiceService.getInvoices();
      
      // ✅ Service already extracts payload, so data is an array
      const invoicesArray = Array.isArray(data) ? data : [];
      
      setInvoices(invoicesArray);
      toast.success('Invoices loaded successfully');
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Refresh invoices with new API call
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchInvoices();
    } finally {
      setRefreshing(false);
    }
  };
  
  // Get date range based on filter selection
  const getDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today': {
        return { start: today, end: new Date(today.getTime() + 86400000 - 1) };
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start: yesterday, end: new Date(yesterday.getTime() + 86400000 - 1) };
      }
      case 'weekly': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return { start: weekAgo, end: today };
      }
      case 'monthly': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: monthStart, end: today };
      }
      case 'q1': {
        const year = today.getFullYear();
        return { start: new Date(year, 0, 1), end: new Date(year, 2, 31, 23, 59, 59) };
      }
      case 'q2': {
        const year = today.getFullYear();
        return { start: new Date(year, 3, 1), end: new Date(year, 5, 30, 23, 59, 59) };
      }
      case 'q3': {
        const year = today.getFullYear();
        return { start: new Date(year, 6, 1), end: new Date(year, 8, 30, 23, 59, 59) };
      }
      case 'q4': {
        const year = today.getFullYear();
        return { start: new Date(year, 9, 1), end: new Date(year, 11, 31, 23, 59, 59) };
      }
      case 'yearly': {
        const yearStart = new Date(today.getFullYear(), 0, 1);
        return { start: yearStart, end: today };
      }
      case 'custom': {
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return null;
      }
      default:
        return null;
    }
  };

  // Get payment status for an invoice
  const getPaymentStatus = (invoice) => {
    const paid = Number(invoice.InvoicePaidAmount) || 0;
    const total = Number(invoice.InvoiceTotalAmount) || 0;
    
    if (paid === 0) return 'notpaid';
    if (paid >= total) return 'paid';
    return 'partial';
  };

  // Get payment methods from an invoice
  const getInvoicePaymentMethods = (invoice) => {
    const methods = new Set();
    
    if (invoice.Payments && invoice.Payments.length > 0) {
      invoice.Payments.forEach(payment => {
        const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name;
        if (methodName) {
          methods.add(methodName.toLowerCase());
        }
      });
    } else if (invoice.PaymentMethod && !invoice.PaymentMethod.includes('/')) {
      methods.add(invoice.PaymentMethod.toLowerCase());
    }
    
    return Array.from(methods);
  };

  // Filter invoices based on all filters
  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      // Payment status filter
      if (paymentStatusFilter !== 'all') {
        const status = getPaymentStatus(invoice);
        if (status !== paymentStatusFilter) return false;
      }

      // Payment method filter
      if (paymentMethodFilter !== 'all') {
        const methods = getInvoicePaymentMethods(invoice);
        if (!methods.includes(paymentMethodFilter.toLowerCase())) return false;
      }

      // Search query filter (customer name, VAT, invoice number)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const customerName = (invoice.CustomerName || invoice.Customer?.Name || '').toLowerCase();
        const customerVAT = (invoice.CustomerVAT || invoice.Customer?.VATNumber || '').toLowerCase();
        const invoiceNumber = (invoice.InvoiceNumber || '').toLowerCase();
        
        if (!customerName.includes(query) && 
            !customerVAT.includes(query) && 
            !invoiceNumber.includes(query)) {
          return false;
        }
      }

      // Date filter
      if (dateFilter && dateFilter !== 'all') {
        const invoiceDate = invoice.CreatedDate;
        
        if (invoiceDate) {
          const dateRange = getDateRange();
          if (dateRange) {
            const invoiceDateObj = new Date(invoiceDate);
            if (invoiceDateObj < dateRange.start || invoiceDateObj > dateRange.end) {
              return false;
            }
          }
        } else {
          return false;
        }
      }

      return true;
    });
  }, [invoices, paymentStatusFilter, paymentMethodFilter, searchQuery, dateFilter]);

  // Reset all filters
  const handleResetFilters = () => {
    setPaymentStatusFilter('all');
    setPaymentMethodFilter('all');
    setSearchQuery('');
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  // ✅ Use shared PDF generation service for consistency
  const handlePreviewInvoicePdf = async (invoice) => {
    setPreviewLoading(true);
    try {
      await generateAndPreviewPDF(invoice, storeInfo, '/invoicetemplates/template-en.html', t);
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    {
      field: 'CustomerId',
      headerName: 'Customer',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const customerName = params.row.CustomerName || params.row.Customer?.Name || '-';
        const customerVAT = params.row.CustomerVAT || params.row.Customer?.VATNumber || '';
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={500}>
              {customerName}
            </Typography>
            {customerVAT && (
              <Typography variant="caption" color="text.secondary">
                {customerVAT}
              </Typography>
            )}
          </Box>
        );
      },
    },
    { field: 'InvoiceNumber', headerName: 'Invoice Number', flex: 1, minWidth: 150 },
    {
      field: 'InvoiceDate',
      headerName: 'Invoice Date',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const invoiceDate = params.row.CreatedDate;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2">
              {formatDate(invoiceDate)}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'InvoiceTotalAmount', 
      headerName: 'Total Amount', 
      flex: 1, 
      align: 'center',
      minWidth: 120,
      headerAlign: 'center',
      renderCell: (params) => {
        const totalAmount = Number(params.row.InvoiceTotalAmount || 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={600}>
              €{totalAmount.toFixed(2)}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'InvoicePaidAmount', 
      headerName: 'Paid Amount', 
      flex: 1, 
      align: 'center',
      minWidth: 120,
      headerAlign: 'center',
      renderCell: (params) => {
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" fontWeight={500} sx={{ color: '#4caf50' }}>
              €{paidAmount.toFixed(2)}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'ProductCount',
      headerName: 'Items',
      flex: 0.6,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.LineItems ? params.row.LineItems.length : 0),
    },
    {
      field: 'IsSendOnPeppol',
      headerName: 'Send on Peppol',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        return (
          <Chip
            icon={isSendOnPeppol ? <CheckCircleIcon /> : <CancelIcon />}
            label={isSendOnPeppol ? 'Yes' : 'No'}
            color={isSendOnPeppol ? 'success' : 'default'}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 70,
            }}
          />
        );
      },
    },
    {
      field: 'PeppolStatus',
      headerName: 'Peppol Status',
      flex: 1,
      minWidth: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isSendOnPeppol = params.row.IsSendOnPeppol;
        const peppolStatus = params.row.PeppolStatus || 'Not Sent';
        
        let icon;
        let color;
        let label;

        if (!isSendOnPeppol) {
          icon = <CancelIcon />;
          color = 'default';
          label = 'N/A';
        } else {
          switch (peppolStatus.toLowerCase()) {
            case 'sent':
            case 'delivered':
            case 'success':
              icon = <CheckCircleIcon />;
              color = 'success';
              label = 'Sent';
              break;
            case 'pending':
            case 'processing':
              icon = <PendingIcon />;
              color = 'warning';
              label = 'Pending';
              break;
            case 'failed':
            case 'error':
              icon = <CancelIcon />;
              color = 'error';
              label = 'Failed';
              break;
            default:
              icon = <PendingIcon />;
              color = 'info';
              label = 'Not Sent';
          }
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: 11,
              minWidth: 85,
            }}
          />
        );
      },
    },
    {
      field: 'InvoiceUrl',
      headerName: 'PDF',
      flex: 0.5,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row ? (
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s'
              }
            }}
            title="Preview PDF"
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewInvoicePdf(params.row);
            }}
          >
            <PictureAsPdfIcon sx={{ color: '#d32f2f', fontSize: 28 }} />
          </Box>
        ) : null,
    },
    {
      field: 'PaymentMethods',
      headerName: 'Payment Methods',
      flex: 1.2,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const payments = params.row.Payments;
        const paymentMethod = params.row.PaymentMethod;
        const paidAmount = Number(params.row.InvoicePaidAmount || 0);
        
        if (payments && payments.length > 0) {
          const aggregated = getAggregatedPayments(payments);
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center', justifyContent: 'center', height: '100%', flexWrap: 'wrap' }}>
              {aggregated.map((payment, index) => (
                <Chip
                  key={index}
                  label={payment.methodName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                />
              ))}
            </Box>
          );
        } else if (paidAmount > 0 && paymentMethod && !paymentMethod.includes('/')) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Chip
                label={paymentMethod}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.75rem', fontWeight: 500 }}
              />
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary">N/A</Typography>
          </Box>
        );
      }
    },
    {
      field: 'Status',
      headerName: 'Payment Status',
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const paid = Number(params.row.InvoicePaidAmount) || 0;
        const total = Number(params.row.InvoiceTotalAmount) || 0;
        let label = '';
        let bg = '';
        if (paid === 0) {
          label = 'Not Paid';
          bg = '#d32f2f';
        } else if (paid >= total) {
          label = 'Paid';
          bg = '#388e3c';
        } else {
          label = 'Partial';
          bg = '#fbc02d';
        }
        return (
          <span
            style={{
              color: '#fff',
              background: bg,
              borderRadius: 10,
              padding: '1px 8px',
              fontWeight: 500,
              fontSize: 11,
              display: 'inline-block',
              minWidth: 60,
              lineHeight: 1.6,
              textAlign: 'center',
              letterSpacing: 0.2,
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button 
          size="small"
          variant="outlined"
          onClick={() => navigate(`/invoices/view/${params.row.Id}`)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 11,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={previewLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} thickness={4} />
          <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 1 }}>
            {t('generating_pdf') || 'Generating PDF...'}
          </Typography>
        </Box>
      </Backdrop>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Invoices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and track all your invoices
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Refresh invoices">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.2) 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.3) 100%)',
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={24} sx={{ color: '#667eea' }} />
                ) : (
                  <RefreshIcon sx={{ color: '#667eea' }} />
                )}
              </IconButton>
            </Tooltip>
            
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => navigate('/invoices/add')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.2s',
                },
              }}
            >
              Add Invoice
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filter Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ fontSize: 24, color: '#667eea', mr: 1 }} />
          <Typography variant="h6" fontWeight="600" sx={{ color: '#667eea' }}>
            Filters
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Search Field */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by customer, VAT, or invoice #"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#667eea' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Payment Status"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="notpaid">Not Paid</MenuItem>
            </TextField>
          </Grid>

          {/* Payment Method Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Payment Method"
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Methods</MenuItem>
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={method.name}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Date Filter */}
          <Grid item xs={12} md={2.25}>
            <TextField
              fullWidth
              select
              size="small"
              label="Date Range"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: '#667eea', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            >
              <MenuItem value="all">All Dates</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="weekly">Last 7 Days</MenuItem>
              <MenuItem value="monthly">This Month</MenuItem>
              {availableQuarters.map((quarter) => (
                <MenuItem key={quarter.value} value={quarter.value}>
                  {quarter.label}
                </MenuItem>
              ))}
              <MenuItem value="yearly">This Year</MenuItem>
              <MenuItem value="custom">Custom Date Range</MenuItem>
            </TextField>
          </Grid>
          
          {/* Custom Date Range */}
          {dateFilter === 'custom' && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              </Grid>
            </>
          )}

          {/* Reset Filters Button - only show when not in custom mode or when custom dates are empty */}
          {dateFilter !== 'custom' && (
            <Grid item xs={12} md={2.25}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  height: '40px',
                  borderRadius: 2,
                  borderColor: '#667eea',
                  color: '#667eea',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          )}
          {dateFilter === 'custom' && (
            <Grid item xs={12} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  height: '40px',
                  borderRadius: 2,
                  borderColor: '#667eea',
                  color: '#667eea',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#667eea',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                  },
                }}
              >
                Reset
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Active Filters Summary */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {(paymentStatusFilter !== 'all' || paymentMethodFilter !== 'all' || searchQuery || dateFilter !== 'all') && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Active Filters:
              </Typography>
              {paymentStatusFilter !== 'all' && (
                <Chip
                  label={`Status: ${paymentStatusFilter === 'notpaid' ? 'Not Paid' : paymentStatusFilter.charAt(0).toUpperCase() + paymentStatusFilter.slice(1)}`}
                  size="small"
                  onDelete={() => setPaymentStatusFilter('all')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {paymentMethodFilter !== 'all' && (
                <Chip
                  label={`Method: ${paymentMethods.find(m => m.name === paymentMethodFilter)?.label || paymentMethodFilter}`}
                  size="small"
                  onDelete={() => setPaymentMethodFilter('all')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={() => setSearchQuery('')}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
              {dateFilter !== 'all' && (
                <Chip
                  label={`Date: ${dateFilter === 'custom' ? `${customStartDate} to ${customEndDate}` : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1).replace(/[qQ]\d/, (m) => m.toUpperCase())}`}
                  size="small"
                  onDelete={() => { setDateFilter('all'); setCustomStartDate(''); setCustomEndDate(''); }}
                  sx={{ backgroundColor: 'rgba(102, 126, 234, 0.1)', color: '#667eea' }}
                />
              )}
            </>
          )}
        </Box>

        {/* Results Count */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing <strong>{filteredInvoices.length}</strong> of <strong>{invoices.length}</strong> invoices
          </Typography>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <ReusableDataGrid
          rows={filteredInvoices}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          pagination
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        />
      </Paper>
    </Container>
  );
};

export default ListInvoices;